import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, FileText, Inbox, Loader2, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { useApi, errMsg } from "@/lib/api";
import { fmtDate } from "@/lib/format";

const ACCEPT = ".eml,.msg,.pdf,.txt,.html,.htm,.png,.jpg,.jpeg,.webp";

async function pollQuote(api, jobId) {
  for (let i = 0; i < 120; i += 1) {
    await new Promise((r) => { setTimeout(r, 3000); });
    let job;
    try { ({ data: job } = await api.get(`/quotes/read/${jobId}`)); } catch (e) { if (e?.response?.status === 404) throw new Error(errMsg(e)); continue; }
    if (job.status === "done") return job.result;
    if (job.status === "error") throw new Error(job.detail);
  }
  throw new Error("Reading the quote took too long. Please try again.");
}

function DropZone({ files, setFiles }) {
  const input = useRef(null);
  const [over, setOver] = useState(false);
  const add = (list) => setFiles((f) => [...f, ...Array.from(list)].slice(0, 8));
  return (
    <div>
      <div role="button" tabIndex={0} onClick={() => input.current.click()} onKeyDown={(e) => { if (e.key === "Enter") input.current.click(); }}
        onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)}
        onDrop={(e) => { e.preventDefault(); setOver(false); add(e.dataTransfer.files); }}
        className={`border border-dashed px-4 py-5 text-center text-xs cursor-pointer transition-colors ${over ? "border-[#5B7FD1] text-white bg-white/[0.03]" : "border-white/15 text-zinc-400 hover:border-white/30"}`}
        data-testid="quote-email-dropzone">
        <Inbox size={18} className="mx-auto mb-2 text-zinc-500" />
        Drop the email here (.eml or Outlook .msg), or a quote PDF / screenshot — or click to choose
      </div>
      <input ref={input} type="file" multiple accept={ACCEPT} className="hidden" onChange={(e) => { add(e.target.files); e.target.value = ""; }} data-testid="quote-email-file-input" />
      {files.length > 0 && (
        <ul className="mt-2 grid gap-1" data-testid="quote-email-files">
          {files.map((f, i) => (
            <li key={`${f.name}-${i}`} className="flex items-center gap-2 text-xs text-zinc-300">
              <FileText size={12} className="text-zinc-500" /> <span className="truncate flex-1">{f.name}</span>
              <button type="button" onClick={() => setFiles((l) => l.filter((_, j) => j !== i))} className="text-zinc-500 hover:text-white" data-testid={`quote-email-file-remove-${i}`}><X size={12} /></button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function TripChoice({ t, onPick, busy }) {
  return (
    <button type="button" disabled={busy} onClick={() => onPick(t)} className="w-full text-left border border-white/10 hover:border-[#5B7FD1] px-3 py-2.5 transition-colors" data-testid={`quote-email-pick-${t.id}`}>
      <span className="block text-sm text-white">{t.ref}</span>
      <span className="block text-[11px] text-zinc-500">{t.route} · {fmtDate(t.date)}{t.broker_name ? ` · ${t.broker_name}` : ""}</span>
    </button>
  );
}

function Choose({ res, trip, onPick, busy }) {
  const ext = res.extracted;
  const route = ext.legs.map((l) => `${l.from} → ${l.to}${l.date ? ` ${fmtDate(l.date)}` : ""}`).join(", ");
  return (
    <div className="grid gap-3" data-testid="quote-email-choose">
      <p className="text-sm text-amber-300 flex gap-2"><AlertTriangle size={15} className="shrink-0 mt-0.5" />
        {trip ? `This quote looks like it's for ${route || "a different route"}, which doesn't match this trip.` : res.candidates.length
          ? `Found ${ext.quotes.length} quote${ext.quotes.length > 1 ? "s" : ""}${route ? ` for ${route}` : ""}. Pick the trip it belongs to:`
          : `No open trip request matches ${route || "this quote"}.`}
      </p>
      {trip && res.candidates.length > 0 && <p className="text-[11px] uppercase tracking-wider text-zinc-500" data-testid="quote-email-other-trips-label">Add it to one of these trips instead:</p>}
      {res.candidates.map((t) => <TripChoice key={t.id} t={t} onPick={onPick} busy={busy} />)}
      {trip && (
        <button type="button" className="frame-btn self-start" disabled={busy} onClick={() => onPick(res.match)} data-testid="quote-email-add-anyway-btn">Add to this trip anyway</button>
      )}
    </div>
  );
}

function Result({ r, showLink }) {
  return (
    <div className="grid gap-2 text-sm" data-testid="quote-email-result">
      {r.added.length > 0 && <p className="text-emerald-400">Added {r.added.length} option{r.added.length > 1 ? "s" : ""} to {r.ref}</p>}
      {r.skipped.map((s) => <p key={s.label} className="text-amber-300 text-xs" data-testid="quote-email-skipped">Skipped {s.label}: {s.reason}</p>)}
      {showLink && <Link to={`/trips/${r.trip_id}`} className="frame-btn gold self-start" data-testid="quote-email-open-trip">Open trip</Link>}
    </div>
  );
}

export default function QuoteFromEmail({ trip, disabled, onAdded, className = "" }) {
  const api = useApi();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState("");
  const [res, setRes] = useState(null);
  const [done, setDone] = useState(null);
  const reset = () => { setText(""); setFiles([]); setRes(null); setDone(null); setBusy(""); };
  const apply = async (target, quotes) => {
    setBusy("Adding options…");
    try {
      const { data } = await api.post(`/trips/${target.id}/quotes/apply`, { quotes });
      if (data.added.length) toast.success(`Added ${data.added.length} option${data.added.length > 1 ? "s" : ""} to ${data.ref}`);
      onAdded?.();
      if (!data.skipped.length && trip) { setOpen(false); reset(); } else setDone(data);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  const read = async () => {
    const fd = new FormData();
    fd.append("text", text);
    if (trip) fd.append("trip_id", trip.id);
    files.forEach((f) => fd.append("files", f));
    setBusy("Reading the quote… this can take up to a minute");
    let data;
    try { data = await pollQuote(api, (await api.post("/quotes/read", fd)).data.job_id); } catch (e) { toast.error(e?.response ? errMsg(e) : e.message); setBusy(""); return; }
    setBusy("");
    if (data.match && !data.mismatch) return apply(data.match, data.extracted.quotes);
    setRes(data);
  };
  return (
    <>
      <button type="button" className={`frame-btn ${className}`} disabled={disabled} title={disabled ? "Acknowledge the request first" : undefined} onClick={() => { reset(); setOpen(true); }} data-testid="quote-email-btn">
        <Inbox size={13} /> {trip ? "Add from email" : "Quote from email"}
      </button>
      <Dialog open={open} onOpenChange={(o) => { if (!busy) setOpen(o); }}>
        <DialogContent className="bg-[#111319] border-white/10 max-w-xl max-h-[92vh] overflow-y-auto" data-testid="quote-email-dialog">
          <DialogHeader><DialogTitle className="font-serif-display text-2xl font-normal text-white">Add options from an operator email</DialogTitle></DialogHeader>
          <p className="text-sm text-zinc-400">{trip ? `Quotes are added to ${trip.ref}.` : "The quote is matched to the open trip request by reference, route and date."} Each aircraft and price becomes an option, ranked by price. Operator details fill in from your Operators list.</p>
          {done ? <Result r={done} showLink={!trip} /> : res ? <Choose res={res} trip={trip} busy={!!busy} onPick={(t) => apply(t, res.extracted.quotes)} /> : (
            <div className="grid gap-3">
              <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={7} placeholder="Paste the operator's quote email here…" className="bg-[#0B0D12] border-white/10" data-testid="quote-email-text" />
              <DropZone files={files} setFiles={setFiles} />
            </div>
          )}
          {busy && <p className="text-xs text-zinc-400 flex items-center gap-2" data-testid="quote-email-busy"><Loader2 size={13} className="animate-spin" /> {busy}</p>}
          <div className="flex justify-end gap-3 pt-1">
            <button type="button" className="frame-btn" disabled={!!busy} onClick={() => setOpen(false)} data-testid="quote-email-close-btn">{done ? "Done" : "Cancel"}</button>
            {!res && !done && (
              <button type="button" className="frame-btn gold" disabled={!!busy || (!text.trim() && !files.length)} onClick={read} data-testid="quote-email-submit-btn">Read & add options</button>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
